export function isMemoriesHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  return hostname === 'memories.dejavunomad.com'
    || hostname === 'memories.localhost'
    || hostname.startsWith('memories.');
}

/** Main Déjà Vu Nomad site (trips, dashboard) — not the Memories subdomain. */
export function mainAppOrigin() {
  if (process.env.REACT_APP_MAIN_APP_URL) {
    return process.env.REACT_APP_MAIN_APP_URL.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'https://dejavunomad.com';
  }
  const { protocol, hostname, port } = window.location;
  if (!isMemoriesHost(hostname)) {
    return window.location.origin;
  }
  if (hostname === 'memories.localhost' || hostname.endsWith('.localhost')) {
    const portPart = port ? `:${port}` : '';
    return `${protocol}//localhost${portPart}`;
  }
  if (hostname.startsWith('memories.')) {
    return `${protocol}//${hostname.replace(/^memories\./, '')}${port ? `:${port}` : ''}`;
  }
  return 'https://dejavunomad.com';
}

export function mainAppTripUrl(tripId, { tab = 'memories' } = {}) {
  const id = encodeURIComponent(String(tripId));
  const query = tab ? `?tab=${encodeURIComponent(tab)}` : '';
  return `${mainAppOrigin()}/trips/${id}${query}`;
}

export function memoriesPath(subpath = '') {
  const clean = `/${String(subpath).replace(/^\//, '')}`.replace(/\/$/, '') || '/';
  if (typeof window !== 'undefined' && isMemoriesHost()) {
    return clean === '/' ? '/' : clean;
  }
  return clean === '/' ? '/memories' : `/memories${clean}`;
}

/**
 * Public base for share/QR/email links.
 * Stay on the current host when already on Memories; otherwise:
 * Trip-linked → main app /memories
 * Standalone → memories subdomain
 */
export function memoriesPublicBase({ tripLinked = false } = {}) {
  if (typeof window !== 'undefined' && isMemoriesHost()) {
    return window.location.origin;
  }
  if (tripLinked) {
    return `${mainAppOrigin()}/memories`;
  }
  if (process.env.REACT_APP_MEMORIES_PUBLIC_URL) {
    return process.env.REACT_APP_MEMORIES_PUBLIC_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (hostname === 'dejavunomad.com' || hostname === 'www.dejavunomad.com') {
      return `${protocol}//memories.dejavunomad.com`;
    }
    // Local: standalone links belong on memories.localhost, not /memories on main
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const portPart = port ? `:${port}` : '';
      return `${protocol}//memories.localhost${portPart}`;
    }
  }
  return 'https://memories.dejavunomad.com';
}

export function mediaUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const apiBase = (process.env.REACT_APP_API_URL
    || (process.env.NODE_ENV === 'production'
      ? 'https://trip-tribe-app-production.up.railway.app'
      : 'http://localhost:5002')).replace(/\/$/, '');
  const root = apiBase.replace(/\/api$/, '');
  return `${root}/api${path.startsWith('/') ? path : `/${path}`}`;
}

function calendarDate(value) {
  if (!value) return null;
  const text = String(value);
  const day = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : null;
  if (!day) return null;
  const [year, month, date] = day.split('-').map(Number);
  const parsed = new Date(year, month - 1, date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatMemoryDate(value) {
  const parsed = calendarDate(value);
  if (!parsed) return '';
  return parsed.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).replace(',', '');
}

export function formatMemoryDateRange(startsOn, endsOn) {
  const start = formatMemoryDate(startsOn);
  const end = formatMemoryDate(endsOn);
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end;
}

export function albumDayLabel(frame, fallback = 'Undated') {
  const parsed = calendarDate(frame?.itinerary_day || frame?.captured_at);
  if (!parsed) return fallback;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const MEMORIES_GUEST_TOKEN_KEY = 'memoriesGuestToken';
export const MEMORIES_GUEST_PROFILE_KEY = 'memoriesGuestProfile';
